/**
 * Display formatters for the queue/drawer (§5 — convert grams→kg only at
 * display time; §18). Pure + DOM-free for the node vitest project.
 */

/** Acronyms that keep their casing when humanizing UPPER_SNAKE codes. */
const ACRONYMS: Record<string, string> = {
  GLS: "GLS",
  DHL: "DHL",
  DPD: "DPD",
  FEDEX: "FedEx",
  UPS: "UPS",
  INTL: "Intl",
  EU: "EU",
  US: "US",
  CA: "CA",
  UK: "UK",
  PSD: "PSD",
  ZPL: "ZPL",
  PDF: "PDF",
  CS: "CS",
  HS: "HS",
};

/**
 * Humanize an UPPER_SNAKE code for display (the enum stays underneath):
 *   GLS_BUSINESS_PARCEL → "GLS Business Parcel"
 *   FEDEX_INTL_PRIORITY → "FedEx Intl Priority"
 * Empty/nullish → "". Used for service codes, carrier codes, etc.
 */
export function humanizeCode(code: string | null | undefined): string {
  if (!code) return "";
  return code
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toUpperCase()] ?? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Alias for clarity at call sites that humanize a carrier service code. */
export const humanizeServiceCode = humanizeCode;

/**
 * Friendly label for a routing/readiness rule. If the value is already a real
 * name (has spaces or an arrow), keep it; if it's an internal slug id
 * (`seed-route-eu-default-gls`), strip the seed/route/readiness prefix and
 * humanize → "EU Default GLS". The raw id stays available via a title attr.
 */
export function humanizeRuleName(idOrName: string | null | undefined): string {
  if (!idOrName) return "";
  if (/[\s→/]/.test(idOrName)) return idOrName;
  return idOrName
    .replace(/^seed-(?:route|readiness|lane|rule)-?/i, "")
    .replace(/^seed-?/i, "")
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toUpperCase()] ?? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Soften a readiness reason for the drawer (§9): the engine writes technical
 * strings like `Requirement not met: Has Flow tag "SHIPPING"` / `Blocked: …`.
 * Turn them into "Not ready — waiting for Flow tag "SHIPPING"" / "Blocked — …".
 */
export function humanizeReadinessReason(effect: string, message: string): string {
  let m = (message ?? "").trim().replace(/^Requirement not met:\s*/i, "").replace(/^Blocked:\s*/i, "");
  if (effect === "BLOCK") return m ? `Blocked — ${m}` : "Blocked";
  // "waiting for X" only reads right when the rule is a "Has <noun>" shape;
  // otherwise keep the rule name verbatim ("Not ready — Address validated").
  const wasHas = /^Has\s+/i.test(m);
  if (wasHas) m = m.replace(/^Has\s+/i, "");
  if (!m) return "Not ready";
  return wasHas ? `Not ready — waiting for ${m}` : `Not ready — ${m}`;
}

interface StoreFields {
  storeRegion?: string | null;
  storeName?: string | null;
  storeId?: string | null;
}
/** A name is "friendly" if it isn't a domain or an opaque cuid we'd show a user. */
function friendly(v: string | null | undefined): string | null {
  if (!v) return null;
  if (v.includes(".") || /myshopify/i.test(v)) return null; // never show a raw domain
  return v;
}
/** Compact store badge text — region (EU/US/CA) preferred; never a domain/cuid. */
export function storeBadge(s: StoreFields): string {
  return s.storeRegion ?? friendly(s.storeName) ?? "—";
}
/** Full store display name — the Store.name, falling back to region, never a domain. */
export function storeName(s: StoreFields): string {
  return friendly(s.storeName) ?? s.storeRegion ?? "—";
}

/** Grams → kg string with 2 dp, e.g. 1234 → "1.23". "—" for null/invalid. */
export function gramsToKg(grams: number | null | undefined): string {
  if (grams == null || !Number.isFinite(grams)) return "—";
  return (grams / 1000).toFixed(2);
}

/**
 * Best weight to show for a queue row: theoretical (§ computed) preferred, else
 * the summed item weight. Returns kg string or "—".
 */
export function weightKg(
  theoreticalWeightG: number | null | undefined,
  itemsWeightG: number | null | undefined,
): string {
  const g = theoreticalWeightG ?? itemsWeightG ?? null;
  return gramsToKg(g);
}

/** Money for the row total. Falls back to a plain number when Intl can't help. */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
